import {
	ColumnDef,
	flexRender,
	getCoreRowModel,
	useReactTable,
} from '@tanstack/react-table';

type TableProps<T extends object> = {
	collection: T[];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	columns: ColumnDef<T, any>[];
	tableStyles?: ITableStyles;
	handleSelectRow?: (item: T) => void;
};

interface ITableStyles {
	tableBox?: string;
	table?: string;
	tableHead?: string;
	tableBody?: string;
	tableBodyTr?: string;
	tableBodyTd?: string;
}

const SimpleTable = <T extends object>({
	collection,
	columns,
	tableStyles,
	handleSelectRow,
}: TableProps<T>) => {
	const {
		tableBox,
		table: tableStyle,
		tableHead,
		tableBody,
		tableBodyTr,
		tableBodyTd,
	} = tableStyles || {};

	const table = useReactTable({
		data: collection,
		columns,
		getCoreRowModel: getCoreRowModel(),
	});

	return (
		<div className={'w-full overflow-x-auto overflow-y-auto ' + tableBox}>
			<div className="min-w-max sticky top-0">
				<table className={'w-full ' + tableStyle}>
					<thead className={'w-full ' + tableHead + '  bg-white'}>
						{table.getHeaderGroups().map((headerGroup) => (
							<tr
								key={headerGroup.id}
								className="border-b text-gray-800 uppercase flex justify-between w-full"
							>
								{headerGroup.headers.map((header) => (
									<th
										key={header.id}
										className="px-4 gap-2 py-4 font-medium text-left whitespace-nowrap"
									>
										{header.isPlaceholder ? null : (
											<div>
												{flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
											</div>
										)}
									</th>
								))}
							</tr>
						))}
					</thead>
				</table>
			</div>
			<div className={'w-full max-h-[500px] ' + tableBody}>
				<table className={'w-full ' + tableStyle}>
					<tbody className={'w-full'}>
						{table.getRowModel().rows.map((row) => (
							<tr
								onClick={
									handleSelectRow
										? () => handleSelectRow(row.original)
										: undefined
								}
								key={row.id}
								className={
									'flex items-center hover:bg-stone-300 h-fit min-h-[40px] flex justify-between ' +
									tableBodyTr
								}
							>
								{row.getVisibleCells().map((cell) => (
									<td
										key={cell.id}
										className={
											'px-4 flex items-center gap-2 whitespace-nowrap ' +
											tableBodyTd
										}
									>
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};

export default SimpleTable;
